
import { useEffect } from 'react';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

const RuralDevelopmentLandOwnershipPage = () => {
  useEffect(() => {
    document.title = 'Rural Development & Land Ownership - Research Report | TS Dotyeni Investments';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Strategic approach to land ownership and development in rural South African contexts. Comprehensive analysis of land reform, ownership structures, and development opportunities.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-emerald-900 to-emerald-700 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/2d88ffbef5ec30192f0941ef0f6b6b2d.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Rural Development & Land Ownership</h1>
            <p className="text-xl leading-relaxed">
              Strategic approach to land ownership and development in rural South African contexts
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Executive Summary */}
        <section className="mb-12">
          <div className="bg-emerald-50 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Executive Summary</h2>
            <p className="text-gray-700 leading-relaxed">
              Land ownership and development in rural South Africa presents unique opportunities and challenges shaped by historical context, ongoing land reform policies, and evolving legal frameworks. This analysis provides strategic insights for investors navigating the complex landscape of rural land acquisition, development rights, and sustainable community-centered development approaches.
            </p>
          </div>
        </section>

        {/* Current Land Ownership Landscape */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Current Land Ownership Landscape</h2>
          
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <img 
              src="https://readdy.ai/api/search-image?query=South%20African%20rural%20land%20ownership%20map%20with%20different%20tenure%20types%2C%20traditional%20authority%20areas%2C%20commercial%20farms%2C%20and%20communal%20lands%2C%20legal%20documentation%20and%20land%20registry%20systems&width=800&height=300&seq=land-landscape&orientation=landscape"
              alt="South African land ownership landscape"
              className="w-full h-48 object-cover object-top rounded-lg mb-4"
            />
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 mb-2">72%</div>
                <div className="text-gray-600">Commercial Farmland</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 mb-2">13%</div>
                <div className="text-gray-600">Communal Land</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 mb-2">15%</div>
                <div className="text-gray-600">State-Owned Land</div>
              </div>
            </div>
          </div>
        </section>

        {/* Land Reform Framework */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Land Reform Framework</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Three Pillars of Land Reform</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-exchange-line text-emerald-600 text-xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Redistribution</h4>
                  <p className="text-gray-600">Transfer of land from white to black ownership through willing buyer, willing seller principle and state acquisition.</p>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-emerald-600 text-xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Restitution</h4>
                  <p className="text-gray-600">Restoration of land rights to those dispossessed after 1913 through the Land Restitution Act.</p>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-file-shield-line text-emerald-600 text-xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Tenure Reform</h4>
                  <p className="text-gray-600">Strengthening of land tenure rights for farm workers, labor tenants, and communal area residents.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Investment Strategies */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Strategic Investment Approaches</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Partnership Models</h3>
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Rural%20South%20African%20community%20partnership%20meeting%2C%20traditional%20leaders%20and%20investors%20discussing%20development%20projects%2C%20collaborative%20planning%20sessions%2C%20community%20engagement%20and%20consultation&width=800&height=300&seq=partnership-models&orientation=landscape"
                  alt="Community partnership models"
                  className="w-full h-48 object-cover object-top rounded-lg mb-4"
                />
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Community Partnerships</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Joint venture structures with traditional authorities
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Benefit-sharing agreements with local communities
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Skills development and employment creation
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Government Collaboration</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Public-private partnership frameworks
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Land development incentive programs
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Infrastructure co-investment opportunities
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Development Approaches</h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Sustainable%20rural%20development%20project%20in%20South%20Africa%2C%20modern%20agricultural%20facilities%20integrated%20with%20traditional%20farming%2C%20renewable%20energy%20installations%2C%20community%20infrastructure%20development&width=800&height=300&seq=development-approaches&orientation=landscape"
                  alt="Sustainable development approaches"
                  className="w-full h-48 object-cover object-top rounded-lg mb-4"
                />
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Integrated Development Model</h4>
                    <p className="text-gray-700">Combining agricultural production, processing facilities, renewable energy, and community infrastructure in comprehensive development projects that create multiple revenue streams while addressing local needs.</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Agri-Industrial Hubs</h4>
                    <p className="text-gray-700">Establishing processing and logistics centers that serve multiple farming communities, creating economies of scale while providing farmers with guaranteed markets and premium pricing.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Legal and Regulatory Framework */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Legal and Regulatory Considerations</h2>
          
          <div className="bg-emerald-50 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Key Legislation and Compliance</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <i className="ri-book-line text-white text-sm"></i>
                </div>
                <div>
                  <strong className="text-gray-900">Land Restitution Act (1994):</strong>
                  <span className="text-gray-700"> Governs the restoration of land rights and establishes procedures for restitution claims.</span>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <i className="ri-book-line text-white text-sm"></i>
                </div>
                <div>
                  <strong className="text-gray-900">Communal Land Rights Act (2004):</strong>
                  <span className="text-gray-700"> Provides for legal security of tenure for communal land residents and establishes land administration systems.</span>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <i className="ri-book-line text-white text-sm"></i>
                </div>
                <div>
                  <strong className="text-gray-900">Spatial Planning and Land Use Management Act (2013):</strong>
                  <span className="text-gray-700"> Establishes uniform framework for spatial planning and land development across all spheres of government.</span>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <i className="ri-book-line text-white text-sm"></i>
                </div>
                <div>
                  <strong className="text-gray-900">Traditional Leadership and Governance Framework Act (2003):</strong>
                  <span className="text-gray-700"> Recognizes traditional leadership and establishes frameworks for cooperation with democratic governance structures.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Risk Assessment */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Risk Assessment & Mitigation</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-red-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-red-800 mb-4">Key Risks</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-alert-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-red-700">Policy uncertainty around land expropriation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-red-700">Complex traditional authority negotiations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-red-700">Infrastructure development costs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-red-700">Community resistance to development</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-green-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-800 mb-4">Mitigation Strategies</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-green-600 mr-2 mt-1"></i>
                  <span className="text-green-700">Early community engagement and consultation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-green-600 mr-2 mt-1"></i>
                  <span className="text-green-700">Comprehensive legal due diligence</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-green-600 mr-2 mt-1"></i>
                  <span className="text-green-700">Phased development approaches</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-green-600 mr-2 mt-1"></i>
                  <span className="text-green-700">Strong government and community partnerships</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Financial Projections */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Financial Projections & Returns</h2>
          
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-emerald-600 mb-2">15-25%</div>
              <div className="text-gray-600">Expected IRR</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-emerald-600 mb-2">5-10 years</div>
              <div className="text-gray-600">Investment Horizon</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-emerald-600 mb-2">$2-50M</div>
              <div className="text-gray-600">Project Scale</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-emerald-600 mb-2">200-2000</div>
              <div className="text-gray-600">Jobs Created</div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Diversification</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-lg font-semibold text-emerald-600">40%</div>
                <div className="text-sm text-gray-600">Agricultural Production</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-emerald-600">35%</div>
                <div className="text-sm text-gray-600">Processing & Value-Add</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-emerald-600">25%</div>
                <div className="text-sm text-gray-600">Infrastructure & Services</div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Strategic Recommendations</h2>
          <div className="bg-gray-50 rounded-2xl p-8">
            <p className="text-gray-700 leading-relaxed mb-6">
              Successful rural land development in South Africa requires a nuanced understanding of historical context, current legal frameworks, and community dynamics. The most promising opportunities lie in collaborative approaches that align investor interests with community development goals while navigating the complex regulatory environment.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our recommended strategy emphasizes early community engagement, comprehensive legal due diligence, phased development approaches, and diversified revenue models that create sustainable value for all stakeholders. With proper planning and execution, rural land development projects can achieve attractive financial returns while contributing meaningfully to South Africa's transformation agenda.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="bg-emerald-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Explore Land Development Opportunities</h3>
            <p className="text-emerald-100 mb-6">
              Contact our land development specialists to discuss strategic opportunities in rural South Africa.
            </p>
            <button className="bg-white text-emerald-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer">
              Schedule Consultation
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RuralDevelopmentLandOwnershipPage;
